/**
 * Creator palettes — the chassis tinted with the vibe the user picked.
 *
 * The structure, labels and actions are the same on every theme (decision D4);
 * only the colours follow the theme, so the creator already feels like the
 * puzzle being made. Values are derived from THEMES in App.jsx, adjusted so
 * small accent text stays readable (≥ 4.5:1 on the page) and dark themes get
 * light ink on dark surfaces.
 *
 * Every key becomes a CSS custom property `--cr-<key>` set on CreatorShell. The
 * tokens in tokens.js read them with the house (romantic) palette as fallback,
 * so anything rendered without a theme — Home, the landing mockups, the first
 * two creator screens — looks exactly as before.
 */

// House palette: Puzzly's own colours, built on romantic. Also the fallbacks.
export const HOUSE = {
  page: "linear-gradient(180deg, #FFF7F3 0%, #FDF1F3 55%, #FBEAEE 100%)",
  blob1: "#FFDDBF", blob2: "#E9DAFB",
  ink: "#3A0C1E", body: "#7E6570", soft: "#86707A", muted: "#6B4F5B", faint: "#9A828C", label: "#B0949F",
  line: "#F3E3E8", dash: "#E2BFCB", disabled: "#D9C8CE", "disabled-bg": "#E4D2D9",
  surface: "#FFFFFF", "surface-alt": "#F7F1F3",
  accent: "#C22F5B", "accent-deep": "#B0204E", "accent-soft": "#FFF1F5", "accent-2": "#CC3D68", "on-accent": "#FFFFFF",
  track: "#F1DCE2", needed: "#F6D3DD", suggested: "#FCEEF2", optional: "#EDE3E7",
  ok: "#5C8C6B", "ok-deep": "#4A7757", badge: "#3A0C1E",
  "shadow-rgb": "58,12,30", "accent-rgb": "194,47,91", "glass-rgb": "255,255,255", "fade-rgb": "251,234,238",
};

export const PALETTES = {
  romantic: HOUSE,
  birthday: {
    page: "linear-gradient(180deg, #FFF6F9 0%, #FEF0F5 55%, #FBE4EE 100%)",
    blob1: "#FFE3B8", blob2: "#D8F3EC",
    ink: "#5A1A3A", body: "#8A5470", soft: "#946A80", muted: "#7D3155", faint: "#A98398", label: "#B88AA0",
    line: "#F7DCE7", dash: "#F2B6CD", disabled: "#E7C9D6", "disabled-bg": "#F3D9E4",
    surface: "#FFFFFF", "surface-alt": "#FDF1F6",
    accent: "#C92E68", "accent-deep": "#A8235A", "accent-soft": "#FFEAF2", "accent-2": "#E0457F", "on-accent": "#FFFFFF",
    track: "#F7D9E5", needed: "#FAD0E0", suggested: "#FDEAF2", optional: "#F1E3EA",
    ok: "#5C8C6B", "ok-deep": "#4A7757", badge: "#5A1A3A",
    "shadow-rgb": "90,26,58", "accent-rgb": "201,46,104", "glass-rgb": "255,255,255", "fade-rgb": "251,228,238",
  },
  funny: {
    page: "linear-gradient(180deg, #FFF6C2 0%, #FFEE8F 55%, #FFE66B 100%)",
    blob1: "#FFD1DF", blob2: "#B8F0EE",
    ink: "#1A0A14", body: "#4A3340", soft: "#5A4350", muted: "#3A1B28", faint: "#7A6450", label: "#8A6A5A",
    line: "#F5DD7A", dash: "#E8B94A", disabled: "#E6D08A", "disabled-bg": "#F2DD85",
    surface: "#FFFBED", "surface-alt": "#FFF4CC",
    accent: "#D11A55", "accent-deep": "#B8134A", "accent-soft": "#FFE0EA", "accent-2": "#FF2E6E", "on-accent": "#FFFFFF",
    track: "#F2D66A", needed: "#FFC7D6", suggested: "#FFF0B8", optional: "#EFE1A6",
    ok: "#2E7D5B", "ok-deep": "#246A4C", badge: "#1A0A14",
    "shadow-rgb": "26,10,20", "accent-rgb": "209,26,85", "glass-rgb": "255,251,237", "fade-rgb": "255,230,107",
  },
  gaming: {
    page: "linear-gradient(180deg, #11103A 0%, #0A0A1F 55%, #07071A 100%)",
    blob1: "#3A1250", blob2: "#0B2A44",
    ink: "#E8E4FF", body: "#A8A0D0", soft: "#B3ACD9", muted: "#CFC9F0", faint: "#8C85B5", label: "#7E78A6",
    line: "#26234F", dash: "#3F3A78", disabled: "#34306A", "disabled-bg": "#221F4A",
    surface: "#16143A", "surface-alt": "#1E1B48",
    accent: "#FF5CCB", "accent-deep": "#FF2EB8", "accent-soft": "#2A1640", "accent-2": "#FF6FD0", "on-accent": "#0A0A1F",
    track: "#26234F", needed: "#3A2A66", suggested: "#221F4A", optional: "#1E1B40",
    ok: "#5EE3B0", "ok-deep": "#3CCB96", badge: "#050514",
    "shadow-rgb": "0,0,0", "accent-rgb": "255,46,184", "glass-rgb": "22,20,58", "fade-rgb": "10,10,31",
  },
  friendship: {
    page: "linear-gradient(180deg, #FCF8EE 0%, #FAF3E3 55%, #F4EAD2 100%)",
    blob1: "#F8E3A6", blob2: "#D5E8D9",
    ink: "#2C3E34", body: "#56705F", soft: "#5F7767", muted: "#4D6857", faint: "#7E9486", label: "#94A99E",
    line: "#EAE2CC", dash: "#C9D9C4", disabled: "#D6DDCF", "disabled-bg": "#E6E2D2",
    surface: "#FFFFFF", "surface-alt": "#F6F1E4",
    accent: "#3D6B4C", "accent-deep": "#2F5A3E", "accent-soft": "#E8F1EA", "accent-2": "#5C8C6B", "on-accent": "#FFFFFF",
    track: "#E3EADF", needed: "#CFE3D4", suggested: "#EEF4EF", optional: "#E6E2D6",
    ok: "#5C8C6B", "ok-deep": "#4A7757", badge: "#2C3E34",
    "shadow-rgb": "44,62,52", "accent-rgb": "61,107,76", "glass-rgb": "255,255,255", "fade-rgb": "244,234,210",
  },
  spooky: {
    page: "linear-gradient(180deg, #221531 0%, #1A1025 55%, #140C1E 100%)",
    blob1: "#3B2356", blob2: "#2A1F4A",
    ink: "#F4E8FF", body: "#C9B8DF", soft: "#B6A5CC", muted: "#D9CBEA", faint: "#9D8BB7", label: "#8B7AA4",
    line: "#3A2A4F", dash: "#5A4675", disabled: "#4A3A5F", "disabled-bg": "#33254A",
    surface: "#2A1C3D", "surface-alt": "#33244A",
    accent: "#CDA8E4", "accent-deep": "#B88DD6", "accent-soft": "#3A2752", "accent-2": "#D8B8EC", "on-accent": "#1A1025",
    track: "#3A2A4F", needed: "#4A3563", suggested: "#33254A", optional: "#2E2240",
    ok: "#8FC7A0", "ok-deep": "#7BB38C", badge: "#120B1C",
    "shadow-rgb": "0,0,0", "accent-rgb": "184,141,214", "glass-rgb": "42,28,61", "fade-rgb": "26,16,37",
  },
  travel: {
    page: "linear-gradient(180deg, #FBF6EA 0%, #F4ECD8 55%, #ECDFC2 100%)",
    blob1: "#F3D98A", blob2: "#CBD6EC",
    ink: "#1C2E52", body: "#4A5E82", soft: "#54678A", muted: "#3D5278", faint: "#7686A3", label: "#8C9BB5",
    line: "#E8DDC4", dash: "#C9CFDD", disabled: "#D6D2C6", "disabled-bg": "#E6DCC6",
    surface: "#FFFAF0", "surface-alt": "#F6EEDC",
    // Stamp red for accents so they stand out from the navy ink; buttons stay navy.
    accent: "#B5402F", "accent-deep": "#1C2E52", "accent-soft": "#F6E6E1", "accent-2": "#34508A", "on-accent": "#FFFFFF",
    track: "#E4DAC2", needed: "#D3DBEA", suggested: "#EFF2F8", optional: "#E6DFCF",
    ok: "#5C8C6B", "ok-deep": "#4A7757", badge: "#1C2E52",
    "shadow-rgb": "28,46,82", "accent-rgb": "181,64,47", "glass-rgb": "255,250,240", "fade-rgb": "236,223,194",
  },
  christmas: {
    page: "linear-gradient(180deg, #FDF9F2 0%, #FBF4EA 55%, #F6E7C8 100%)",
    blob1: "#F2DDA8", blob2: "#D3E4D8",
    ink: "#1E3A2B", body: "#4F6658", soft: "#56695D", muted: "#3F5A4A", faint: "#7A8C80", label: "#8E9E93",
    line: "#EADFCB", dash: "#D9C9A8", disabled: "#DCD6C6", "disabled-bg": "#ECE3CF",
    surface: "#FFFFFF", "surface-alt": "#FBF4EA",
    // Cranberry for buttons and accents, pine as the second accent, gold kept for the art.
    accent: "#B3263A", "accent-deep": "#9A2032", "accent-soft": "#FBE9EB", "accent-2": "#1E4D36", "on-accent": "#FFFFFF",
    track: "#EDE3CC", needed: "#F4D6DA", suggested: "#FBF0F1", optional: "#ECE5D4",
    ok: "#1E4D36", "ok-deep": "#163B2A", badge: "#1E3A2B",
    "shadow-rgb": "30,58,43", "accent-rgb": "179,38,58", "glass-rgb": "255,255,255", "fade-rgb": "246,231,200",
  },
};

/** CSS custom properties for a theme; empty (house fallbacks) when there is none. */
export function paletteVars(theme) {
  const p = theme && PALETTES[theme];
  if (!p) return {};
  const vars = {};
  for (const [k, v] of Object.entries(p)) vars[`--cr-${k}`] = v;
  return vars;
}

/** Whether a theme's chassis is dark (light ink on dark surfaces). */
export const isDarkPalette = (theme) => theme === "gaming" || theme === "spooky";
